import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import Header from './Header';
import './EmailVerification.css';

interface LocationState {
  email: string;
  formData: Record<string, string>;
}

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedRef = useRef(false);

  const email = state?.email;
  const formData = state?.formData;

  useEffect(() => {
    // Redirect to home if no email provided
    if (!email) {
      navigate('/', { replace: true });
      return;
    }

    // Prevent double initialization
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    // Mark that verification is in progress to prevent going back
    sessionStorage.setItem('verificationInProgress', 'true');

    // Don't send verification code here - it was already sent from the form submission
    // Just set initial timer (15 minutes default)
    setTimeRemaining(900);
    
    // Check session status periodically
    checkSessionStatus();
    const sessionInterval = setInterval(checkSessionStatus, 30000); // Check every 30 seconds

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      clearInterval(sessionInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once

  useEffect(() => {
    // Handle countdown timer
    if (timeRemaining && timeRemaining > 0) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev && prev <= 1) {
            setSessionExpired(true);
            return 0;
          }
          return prev ? prev - 1 : 0;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [timeRemaining]);

  const checkSessionStatus = async () => {
    if (!email) return;
    
    try {
      const sessionData = await apiService.getSessionStatus(email);
      if (sessionData.session_info) {
        setTimeRemaining(sessionData.session_info.time_remaining);
        if (sessionData.session_info.time_remaining <= 0) {
          setSessionExpired(true);
        }
      }
    } catch (err: any) {
      console.error('Error checking session status:', err);
      if (err.status === 404) {
        setSessionExpired(true);
      }
    }
  };

  const sendVerificationCode = async () => {
    if (!email) return;
    
    try {
      setError(null);
      const response = await apiService.sendVerificationCode({ email });
      setTimeRemaining(response.expires_in || 900); // Default 15 minutes
      
      // Start resend cooldown
      setResendCooldown(30);
      const cooldownInterval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(cooldownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...verificationCode];
    
    for (let i = 0; i < 6; i++) {
      newCode[i] = pastedText[i] || '';
    }
    
    setVerificationCode(newCode);
    setError(null);
    
    // Focus the next empty input or the last one
    const nextEmptyIndex = newCode.findIndex(digit => !digit);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerifyCode = async () => {
    if (!email) return;
    
    const code = verificationCode.join('');
    console.log('Attempting to verify code:', { email, code, codeLength: code.length });
    
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit verification code');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const response = await apiService.verifyCode({ email, code });
      
      if (response && response.verified === true) {
        sessionStorage.removeItem('verificationInProgress');
        
        console.log('✅ Verification successful!', { formData, email });

        // User responses are already saved, just proceed to analysis
        navigate('/analysis-loading', { 
          state: { email, formData },
          replace: true 
        });
      } else {
        console.log('❌ Verification failed - response.verified is not true');
        console.log('Actual value:', response?.verified);
        setError('Verification failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Verification error:', err);
      console.log('Error details:', {
        status: err.status,
        message: err.message,
        response: err.response,
        data: err.response?.data
      });
      
      if (err.status === 410) {
        setError('Verification code has expired. Please request a new one.');
      } else if (err.status === 400) {
        setError('Invalid verification code. Please check and try again.');
      } else {
        setError(err.message || 'Failed to verify code');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCancel = () => {
    navigate('/', { replace: true });
  };

  const handleChangeEmail = () => {
    navigate('/', { state: { prefillData: formData }, replace: true });
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const isCodeComplete = verificationCode.every(digit => digit !== '');

  if (sessionExpired) {
    return (
      <div className="verification-container">
        <div className="verification-card">
          <div className="verification-header">
            <h1>Session Expired</h1>
            <p>Your verification session has expired for security reasons.</p>
          </div>
          
          <div className="session-expired-content">
            <div className="expired-icon">⏰</div>
            <p>Please start over and enter your information again to continue with your AI ranking check.</p>
            
            <button 
              onClick={() => navigate('/', { replace: true })}
              className="btn btn-primary"
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="verification-container">
      <Header />
      <div className="verification-card">
        <div className="verification-header">
          <h1>Verify Your Email</h1>
          <p>Before we can show you your AI ranking, we need to verify your email address</p>
          <p className="verification-subtitle">
            Email verification helps us prevent spam and ensure our AI resources are used responsibly
          </p>
        </div>

        <div className="verification-notice">
          <div className="notice-icon">!</div>
          <span>Check your email for a verification from PlumberSEO</span>
        </div>

        <div className="verification-form">
          <div className="code-section">
            <label>Enter the 6-digit code sent to {email}</label>
            <div className="code-inputs">
              {verificationCode.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className={`code-input ${error ? 'error' : ''}`}
                  disabled={isVerifying}
                />
              ))}
            </div>
            <p className="code-info">
              The code is 6 digits long and expires in {timeRemaining ? formatTime(timeRemaining) : '15:00'} minutes
            </p>
            {error && <p className="error-message">{error}</p>}
          </div>

          <div className="verification-actions">
            <button
              onClick={handleCancel}
              className="btn btn-secondary"
              disabled={isVerifying}
            >
              Cancel
            </button>
            <button
              onClick={handleVerifyCode}
              className="btn btn-primary"
              disabled={!isCodeComplete || isVerifying}
            >
              {isVerifying ? (
                <>
                  <span className="spinner"></span>
                  Verifying...
                </>
              ) : (
                'Verify Email'
              )}
            </button>
          </div>

          <div className="verification-links">
            <div className="resend-section">
              <span>Didn't receive a code? </span>
              <button
                onClick={sendVerificationCode}
                disabled={resendCooldown > 0}
                className="link-button"
              >
                {resendCooldown > 0 ? `Send again (${resendCooldown}s)` : 'Send again'}
              </button>
            </div>
            <div className="change-email-section">
              <span>Wrong email address? </span>
              <button onClick={handleChangeEmail} className="link-button">
                Change Email
              </button>
            </div>
          </div>
        </div>

        <div className="verification-info">
          <h3>Why we need to verify your email</h3>
          <ul className="verification-reasons">
            <li>
              <span className="reason-icon">•</span>
              Prevent spam and abuse of our free AI service
            </li>
            <li>
              <span className="reason-icon">•</span>
              Ensure our AI resources are used responsibly
            </li>
            <li>
              <span className="reason-icon">•</span>
              Protect the quality of our service for legitimate businesses
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
